import {
  addMonths as addMonthsToDate,
  differenceInCalendarDays,
  format,
  isValid,
  lastDayOfMonth,
  parseISO,
} from 'date-fns';

/**
 * Transforms a date string to a locale date string.
 *
 * ex: '2024-09-26T07:00:00.000Z' -> '9/26/2024'
 */
export function formatDateToLocaleString(date: string): string {
  if (Date.parse(date)) {
    return new Date(date).toLocaleDateString('en-US');
  }

  return date;
}

/**
 * Takes a time that is in UTC, and converts it to the local timezone with a locale string.
 *
 * ex: '2024-09-26T15:00:00.000Z' -> '9/26/2024, 8:00 AM'
 */
export function formatUTCDateTimeToLocaleString(utcDateString: string): string {
  const utcDate = new Date(utcDateString);
  const options: Intl.DateTimeFormatOptions = {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  };

  return utcDate.toLocaleString('en-US', options);
}

/**
 * Formats a date string as M/DD/YY (short date with 2-digit year).
 *
 * ex: '2024-09-26T07:00:00.000Z' -> '9/26/24'
 */
export function formatDateShort(date: string): string {
  const d = new Date(date);
  const month = d.getMonth() + 1;
  const day = String(d.getDate()).padStart(2, '0');
  const year = String(d.getFullYear()).slice(-2);

  return `${month}/${day}/${year}`;
}

/**
 * formats a date for a file name
 *
 * ex: '2024-09-26T15:00:00.000Z' -> '10-29-2024 1-27 PM'
 */
export function formatDateTimeForFileName(date: string): string {
  const utcDate = new Date(date);
  const options: Intl.DateTimeFormatOptions = {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };

  const formattedDate = utcDate.toLocaleString('en-US', options);

  return formattedDate
    .replace(/\//g, '-') // replace slashes with dashes
    .replace(/, /g, ' ') // remove comma
    .replace(':', '-'); // replace colon in time with dash
}

/** Parse `YYYY-MM-DD` to a local-midnight Date (avoids the UTC shift of `new Date(iso)`). */
export function parseISODate(iso: string): Date {
  const date = parseISO(iso);

  if (!isValid(date)) {
    throw new Error(`Invalid ISO date: ${iso}`);
  }

  return date;
}

/** Format a Date as `YYYY-MM-DD` from its local fields (never touches UTC). */
export function toISODate(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/** First day of a `YYYY-MM` as a local-midnight Date. */
function startOfISOMonth(month: string): Date {
  const date = parseISO(month);

  if (!isValid(date)) {
    throw new Error(`Invalid month: ${month} (expected YYYY-MM)`);
  }

  return date;
}

/** Split a `YYYY-MM` into its numeric year and 0-based month index. */
export function parseMonth(month: string): { year: number; monthIndex: number } {
  const date = startOfISOMonth(month);

  return { year: date.getFullYear(), monthIndex: date.getMonth() };
}

/** The `YYYY-MM` an ISO date belongs to. */
export function monthOf(iso: string): string {
  return format(parseISODate(iso), 'yyyy-MM');
}

/** Shift a `YYYY-MM` by whole months, wrapping the year as needed. */
export function addMonths(month: string, delta: number): string {
  return format(addMonthsToDate(startOfISOMonth(month), delta), 'yyyy-MM');
}

/** `2026-06` → `June 2026`. */
export function formatMonthLabel(month: string): string {
  return format(startOfISOMonth(month), 'MMMM yyyy');
}

/** The first–last day span of a month, e.g. `Jun 1, 2026 – Jun 30, 2026`. */
export function formatMonthRange(month: string): string {
  const firstDay = startOfISOMonth(month);

  return `${format(firstDay, 'MMM d, yyyy')} – ${format(lastDayOfMonth(firstDay), 'MMM d, yyyy')}`;
}

/** `2026-08-04` → `Aug 4, 2026`. */
export function formatLongDate(iso: string): string {
  return format(parseISODate(iso), 'MMM d, yyyy');
}

/** Full weekday name for an ISO date, e.g. `2025-01-10` → `Friday`. */
export function formatWeekday(iso: string): string {
  return format(parseISODate(iso), 'EEEE');
}

/** Whole days from `from` to `to` (negative if `to` precedes `from`). */
export function daysBetween(from: string, to: string): number {
  return differenceInCalendarDays(parseISODate(to), parseISODate(from));
}

/** A stacked month/day pair, e.g. `{ month: 'JUN', day: 26 }`. */
export function dayBadge(iso: string): { month: string; day: number } {
  const date = parseISODate(iso);

  return { month: format(date, 'MMM').toUpperCase(), day: date.getDate() };
}

/** Lexical comparison is correct for fixed-width `YYYY-MM-DD` strings. */
export function compareISO(a: string, b: string): -1 | 0 | 1 {
  if (a < b) return -1;
  if (a > b) return 1;

  return 0;
}
